import { Typography, Stack, Box } from "@mui/material";
import { Notification } from "./Notification";
import { formatRelativeTime } from "../../../Utils/TimeUtils";
import { useResource } from "../../../Translations/Resources";
import {
  groupNotificationsByAge,
  resolveTranslatableText,
} from "Components/Shared/Notification/utils";
import { NoNotifications } from "Components/Shared/Notification/NoNotifications";
import { useMemo } from "react";
import { useNotifications } from "Api/Queries/Notifications/useNotifications";
import { useMarkNotificationAsSeen } from "Api/Mutations/Notifications/useMarkNotificationAsSeen";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";

type Props = {
  isInPopup?: boolean;
  isReadOnly?: boolean;
};

export const NotificationsList = ({
  isInPopup = false,
  isReadOnly = false,
}: Props) => {
  const { data: notificationsData, isLoading } = useNotifications(
    isInPopup, // new only in popup
    1,
    50,
  );
  const { t } = useResource();
  const { mutate: markAsSeen } = useMarkNotificationAsSeen();
  const handleDismissNotification = (userNotificationPublicID: string) => {
    markAsSeen(userNotificationPublicID);
  };

  const groupedNotifications = useMemo(
    () => groupNotificationsByAge(notificationsData?.items || [], t),
    [notificationsData, t],
  );

  if (isLoading) {
    return <CenteredCircularProgress color="grey" />;
  }

  if (groupedNotifications.length === 0) {
    return <NoNotifications />;
  }

  return (
    <Box
      pr={2}
      overflow="auto"
      maxHeight={isInPopup ? "min(600px, calc(100vh - 144px))" : "none"}
    >
      <Stack gap={11} overflow="hidden">
        {groupedNotifications.map(({ title, notifications }) => (
          <Stack key={title} gap={1}>
            <Typography
              fontWeight={600}
              variant={isInPopup ? "subtitle1" : "h6"}
            >
              {title}
            </Typography>
            <Stack gap={2}>
              {notifications.map(
                ({
                  publicID: userNotificationPublicID,
                  title,
                  content,
                  scheduledDate,
                  variant,
                  link,
                }) => (
                  <Notification
                    key={userNotificationPublicID}
                    name={resolveTranslatableText(title)}
                    content={resolveTranslatableText(content)}
                    variant={variant}
                    withPaper={!isInPopup}
                    link={link}
                    time={formatRelativeTime(scheduledDate, t)}
                    onClose={
                      isReadOnly
                        ? undefined
                        : () =>
                            handleDismissNotification(userNotificationPublicID)
                    }
                  />
                ),
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
